import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import type { BaseSyntheticEvent } from 'react';
import { useCallback, useContext, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import type { FormState, SubmitHandler, UseFormRegister } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { postAccountIntegrateSendVerificationEmail } from '../../../api/apiClient';
import { allowDomains, disallowDomains } from '../../../utils/env';
import { SnackbarContext } from '../../../utils/snackbar/snackbarProvider';
import { getCurrentTimestampMs } from '../../../utils/time';
import { validateAllowDomains } from '../../emailChange/utils/validateAllowDomains';
import { validateDisallowDomains } from '../../emailChange/utils/validateDisallowDomains';

export const inputs = z.object({
  email: z
    .string()
    .min(1, { message: 'メールアドレスの形式が正しくありません' })
    .email('メールアドレスの形式が正しくありません')
    .refine(
      (email) => {
        const [, domain] = email.split('@');
        // emailバリデーションを通っているので、emailの中に必ず@は入っていてsplitにより配列の要素は２つになる想定
        // 許可リストに含まれていることを確認した上で、禁止リストに含まれていることを確認する流れとしているため && でチェックを行う
        return validateAllowDomains(allowDomains, domain!) && validateDisallowDomains(disallowDomains, domain!);
      },
      (email) => ({ message: `${email.split('@')[1]} が含まれるメールアドレスはご利用いただけません` })
    ),
});
export type InputsType = z.infer<typeof inputs>;

export type UseIntegrationFormProps = {
  isSuccess: boolean;
  register: UseFormRegister<InputsType>;
  onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
  formState: FormState<InputsType>;
  email: string;
};

export type UseIntegrationFormOptions = {
  // 入力メールが既存アカウントの場合に、そのメールを login_hint として既存アカウントのログインへ誘導する
  onExistingAccount: (email: string) => void;
  // 統合完了後にログイン元アプリへ戻すため、Actionが付与した client_id を callbackClientId として送信メールAPIへ引き回す
  callbackClientId?: string;
};

export function useIntegrationForm({ onExistingAccount, callbackClientId }: UseIntegrationFormOptions): UseIntegrationFormProps {
  const { showBoundary } = useErrorBoundary();
  const snackbar = useContext(SnackbarContext);
  const { mutateAsync } = useMutation({
    mutationFn: (email: string) => postAccountIntegrateSendVerificationEmail({ email, callbackClientId }),
  });
  const { register, handleSubmit, formState } = useForm<InputsType>({
    resolver: zodResolver(inputs),
    mode: 'onBlur',
  });
  const [isSuccess, setIsSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const onSubmit: SubmitHandler<InputsType> = useCallback(
    async (data) => {
      const result = await mutateAsync(data.email);
      // 既にアカウントが存在するメールアドレスの場合は確認メールが送信されないため、既存アカウントのログインへ誘導する
      if (result.response.status === 200) {
        onExistingAccount(data.email);
        return;
      }
      if (result.response.ok) {
        setIsSuccess(true);
        setEmail(data.email);
        return;
      }
      if (result.response.status === 409) {
        snackbar.add({
          id: getCurrentTimestampMs(),
          message: '入力されたメールアドレスは使用できません。別のメールアドレスを入力してください。',
          notificationLevel: 'error',
          hideCloseButon: false,
        });
        return;
      }
      showBoundary(result.error);
    },
    [mutateAsync, onExistingAccount, showBoundary, snackbar]
  );
  return {
    register,
    isSuccess,
    email,
    onSubmit: handleSubmit(onSubmit),
    formState,
  };
}
